import useAnalytics from './useAnalytics'
import { useEffect, useRef } from 'react'
import { getShopKey } from '@dupe/common/helpers'
import { AnalyticsEventName } from '@dupe/common/constants'
import { useRouter } from 'next/router'
import poll from '~/src/lib/poll'
import { QUERY_TYPE } from '~/src/lib/brain/performedDupeChatProps'
import type { TrackedCategoryGroup } from '~/src/lib/affiliateRouter'
import {
  trackGoogleAdsDupePerformed,
  type DupeSearchType,
} from '~/src/lib/analytics/googleAdsConversions'
import { trackMetaAdsConversion } from '~/src/lib/analytics/metaAdsConversion'
import { trackMetaDupePerformed } from '~/src/lib/analytics/metaConversions'
import { trackOpenAiDupePerformed } from '~/src/lib/analytics/openAiConversions'
import { trackTikTokDupePerformed } from '~/src/lib/analytics/tiktokConversions'
import { dailyConversionId } from '~/src/lib/analytics/dailyConversionId'
import {
  deriveSearchOutcome,
  readSearchContextProps,
} from '~/src/lib/analytics/searchOutcome'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'

/**
 * The outcome of the search, as known at the moment the event fires.
 *
 * Split out from the rest of the props because it is the one part that is NOT
 * knowable when the page mounts: the dupes are still in flight. The hook reads
 * it through a ref at fire time, so the values are whatever the page has
 * settled on by then rather than whatever it had on the first render.
 */
export interface PerformedDupeOutcome {
  /**
   * True once the dupes request has actually COMPLETED (data arrived, or it
   * errored).
   *
   * ⚠️ Must be a positive completion signal, not `!isLoading`. "Not currently
   * loading" is also true in the instant before the request starts, so a
   * negated loading flag reports a finished search with zero results on every
   * page load — verified in the browser, not theorised.
   */
  ready: boolean
  /**
   * Whether the search query was ever ENABLED.
   *
   * ⚠️ The distinction `ready` cannot make, and the whole reason this field
   * exists. A query that never ran and a query that ran and found nothing look
   * identical from every other signal: both hold no data, both report
   * `isLoading: false` (a disabled TanStack v5 query is `isPending: true,
   * isFetching: false`, and `isLoading = isPending && isFetching`), and both
   * count zero results. Only the gates themselves can tell them apart, so the
   * caller evaluates the same gates its query does and reports the answer here.
   *
   * `false` reports the counts as UNKNOWN, exactly as an error does — a search
   * nobody ever issued has not "found nothing".
   *
   * Omitted = this surface's query is always enabled (today's callers). It is
   * never inferred.
   */
  enabled?: boolean
  /**
   * Whether a country had resolved when the event fired.
   *
   * `useGetDupes` gates its fetch on `!!selectedCountry`, so no country is one
   * concrete way a search is never issued. Recorded alongside the outcome so
   * the causes stay separable on surfaces that cannot see the query's error.
   */
  countryKnown?: boolean
  /** Which phase the counts came from. `quick` results are real but not final
   * (the full response can add more), so a reader comparing counts across
   * events needs to know which they're looking at. */
  phase: 'full' | 'quick' | 'error' | null
  /** Organic result count. Only meaningful once `ready`. */
  resultsCount: number
  /** Curated "Our picks" count, counted separately — a search that returned
   * only picks is not the same as one that returned nothing.
   *
   * `null` when this surface does not split picks out of its match list. The
   * total still reports honestly; `organic_results_count` and
   * `picks_results_count` go null rather than claiming a split that was never
   * computed. */
  picksCount: number | null
  /** Price of the product being duped (the anchor), in `priceAnchorCurrency`. */
  priceAnchor: number | null
  priceAnchorCurrency: string | null
  /** What the user actually searched for. */
  query: string | null
}

/**
 * A measured-but-unknown outcome. Used when the page has moved on to another
 * search before the previous one's event fired: the surface DOES measure
 * counts, we just no longer have this search's.
 */
const UNSETTLED_OUTCOME: PerformedDupeOutcome = {
  ready: false,
  phase: null,
  resultsCount: 0,
  picksCount: 0,
  priceAnchor: null,
  priceAnchorCurrency: null,
  query: null,
}

/**
 * The outcome half of the payload, or NOTHING when the caller passed no
 * `outcome`.
 *
 * The distinction matters. Three surfaces call this hook and only the results
 * page can see what the search returned; the other two never will. Emitting
 * `results_count: null` there would claim "this search's count is unknown"
 * when the truth is "this surface does not measure counts at all" — and it
 * would quietly widen every filter built on the property's presence. An absent
 * key says that correctly and costs nothing.
 *
 * Within a surface that DOES supply an outcome, `null` keeps its meaning:
 * the search hadn't settled in time. Absent = not measured here; null =
 * measured, not known yet; 0 = a real zero-result search.
 */
/**
 * How long a shut gate has to stay shut before `never_ran` is a verdict.
 *
 * Deliberately under `poll`'s own 3s cap, so a never-enabled search — which
 * never becomes `ready` and therefore always rides that cap — still reaches a
 * verdict. The grace exists for the OTHER exit: the `pagehide` / unmount
 * flush, which can fire in the first moments, before the country has had any
 * chance to resolve.
 *
 * 3s is all the window this surface has. A gate that opens later than that is
 * reported as `never_ran` with `country_known: false` alongside it, so the
 * cause is at least visible — see the PR for why the cap is not extended.
 */
const GATE_GRACE_MS = 2500

const buildOutcomeProps = (
  outcome: PerformedDupeOutcome | undefined,
  /**
   * Whether the gates behind `enabled` have had TIME to resolve.
   *
   * ⚠️ Without this the measurement repeats the mistake it exists to catch.
   * The country resolves from a cookie or, failing that, a `/api/geo` round
   * trip — and in the side panel dupe.com runs in a cross-site iframe where
   * the cookie can be unreachable, so geo is the common path. A shut gate read
   * before it had a chance to open is not evidence of anything, and the honest
   * answer there is `unknown`, not `never_ran`.
   */
  gatesResolved = true
): Record<string, unknown> => {
  if (!outcome) return {}

  // A failed dupes request is NOT a zero-result search. Both leave the counts
  // at zero, but they mean opposite things: one is "we looked and there is
  // nothing", the other is "we never got to look". Only the first belongs in a
  // zero-result recovery journey — during an API outage the second would email
  // a wave of people whose search would have worked fine. So an errored search
  // reports its counts as UNKNOWN and flags itself, and `zero_results` is
  // reserved for a response that actually came back empty.
  const failed = outcome.phase === 'error'
  // A search that was never issued is in the same bucket as a failed one, and
  // for the same reason: its counts are UNKNOWN, not empty. ⛔ Do not let this
  // fall through to the zero branch — "never ran" reported as `zero_results:
  // true` is the exact defect this field was added to measure, and once the
  // two share a value no query can separate them again.
  const enabledRaw = outcome.enabled ?? true
  // A query that positively SETTLED is evidence in its own right: data or an
  // error arrived, so nothing is still resolving and the time-based grace has
  // nothing left to protect. Folding `ready` in here also keeps the payload
  // self-consistent — without it a settled search could report
  // `search_outcome: 'unknown'` next to `zero_results: true`.
  const gatesKnown = gatesResolved || outcome.ready
  const rawCount = outcome.resultsCount + (outcome.picksCount ?? 0)
  // ...unless there are rows on screen, which no gate reading can unsay. A
  // search that produced results obviously ran, and nulling its count here
  // would leave the payload arguing with itself: `search_outcome: 'results'`
  // next to `results_count: null`.
  const neverRan = !enabledRaw && gatesKnown && rawCount === 0
  const isReady = outcome.ready && !failed && !neverRan
  const resultsCount = isReady ? rawCount : null

  return {
    /**
     * The four real states plus honest ignorance, in one property:
     * `results` | `zero` | `failed` | `never_ran` | `unknown`.
     *
     * The existing booleans below cannot express `never_ran` — they were
     * built when "the search ran" was taken for granted — and widening either
     * one to carry it would silently change what every filter already built
     * on them means. A new property with its own vocabulary costs nothing and
     * breaks nothing.
     */
    search_outcome: deriveSearchOutcome({
      // A gate that has not had its chance reports as enabled-but-unsettled,
      // which lands on `unknown`. ⚠️ The grace applies ONLY to a shut gate — a
      // search whose gates are all open is judged on `ready` however early we
      // look, or every fast cached result would be filed as unknown.
      enabled: enabledRaw || !gatesKnown,
      settled: outcome.ready && (enabledRaw || gatesKnown),
      failed,
      // The raw count when there is one, so products already on screen settle
      // the question even if a gate reads false on this render.
      resultsCount: rawCount > 0 ? rawCount : resultsCount,
    }),
    /** Whether a country had resolved. One concrete cause of `never_ran`
     * (`useGetDupes` gates its fetch on it), kept separate so the cause stays
     * readable where the query's own error is not available. Null = this
     * surface does not report it. */
    country_known: outcome.countryKnown ?? null,
    // What the user searched for. The results page is the single biggest
    // `Performed Dupe` emitter and never carried this, which is most of why
    // only ~27% of search events had query text.
    q: outcome.query ?? null,
    // Price of the product being duped. Every "you saved X%" claim needs an
    // anchor to compare the dupe's price against; without it the saving can
    // only be computed by re-fetching the source product at send time.
    price_anchor: outcome.priceAnchor ?? null,
    price_anchor_currency: outcome.priceAnchorCurrency ?? null,
    // Null = the search hadn't settled in time (unknown).
    // 0 = a real zero-result search.
    results_count: resultsCount,
    // Both go null when the surface did not split picks out of its match list
    // (`picksCount: null`). The total above is still right; claiming a split
    // that was never computed would report every pick as organic.
    organic_results_count:
      isReady && outcome.picksCount !== null ? outcome.resultsCount : null,
    picks_results_count: isReady ? outcome.picksCount : null,
    zero_results: resultsCount === null ? null : resultsCount === 0,
    // 'quick' = the fast first-phase response; the full one may still add
    // results. 'error' = the request failed. Don't pool them when comparing
    // counts, and never read an 'error' row as a search that found nothing.
    results_phase: outcome.ready ? (outcome.phase ?? null) : null,
    // true = the dupes request failed, so the counts above are unknown rather
    // than empty. null = the search hadn't settled either way yet.
    search_failed: outcome.ready ? failed : null,
  }
}

const useTrackPerformedDupe = ({
  hash,
  dupeParam,
  urlParam,
  threadId,
  chat,
  categoryGroup,
  outcome,
  searchType,
  trackAdsConversions = false,
}: {
  hash: string | null | undefined
  dupeParam: string
  urlParam?: string
  threadId?: string | null
  chat?: boolean
  // Router category lane for this search (fashion/beauty/furniture_home/rugs,
  // or `other` for a classified non-lane category), so top-of-funnel search
  // volume is sliceable by the same lanes the toggler routes click-outs to.
  // Best-effort at fire time; null when never categorized.
  categoryGroup?: TrackedCategoryGroup | null
  /** Search outcome (result count, anchor price, query text). Optional so
   * surfaces that genuinely cannot know it keep today's payload. */
  outcome?: PerformedDupeOutcome
  /** How this search was started. Settles asynchronously on the results page
   *  (it comes back with the dupes response), so it is read through a ref at
   *  fire time — see `searchTypeRef`. */
  searchType?: DupeSearchType
  /**
   * Opt IN to the manual ad-platform search conversions — Google Ads
   * (DUP-2080) and Meta (DUP-2091 — the browser pixel AND
   * `PerformedDupeAdsConversion` → the PostHog "Meta Ads Conversions" CAPI
   * destination, both from this one firing decision so the platforms can
   * never drift apart).
   *
   * Defaults to false so adding the call to this shared hook did not silently
   * enrol every surface that uses it. Scoped to the three dupe.com entry
   * paths (text query, image upload, `dupe.com/<url>`); the extension
   * sidepanel results surface also calls this hook and is NOT ads landing
   * traffic, so it stays out.
   */
  trackAdsConversions?: boolean
}) => {
  const router = useRouter()
  const analytics = useAnalytics()
  const { tempUserId } = useGetTempUserId()

  // The poll callback below runs up to 3s after the effect, so reading
  // `outcome` from the closure would report the page's FIRST render — which is
  // exactly the moment the dupes have not arrived yet. A ref keeps the read
  // live without re-running (and re-firing) the effect.
  //
  // Stored WITH its hash. A client-side navigation to another result page
  // re-renders this same component rather than unmounting it, so a poll still
  // pending from the previous search would otherwise read the NEW search's
  // query and counts and report them under the OLD search's hash — a wrong
  // answer that looks entirely well-formed.
  const outcomeRef = useRef<{
    hash: string | null | undefined
    outcome: PerformedDupeOutcome | undefined
  }>({ hash, outcome })
  outcomeRef.current = { hash, outcome }

  // Same live-read problem as `outcome`, same fix. On the results page
  // `searchType` is derived from the dupes response (`inputType`), which is
  // still null when this effect runs — so a closure capture reports every
  // search as `text` and an image search could never be labelled.
  const searchTypeRef = useRef<{
    hash: string | null | undefined
    searchType: DupeSearchType | undefined
  }>({ hash, searchType })
  searchTypeRef.current = { hash, searchType }

  // Same live-read problem, and the most costly one to get wrong.
  // `useGetTempUserId` returns undefined on the first render for every visitor
  // without the cookie, filling in only after its own effect plus a re-render
  // — but this effect's deps are `[urlParam, hash, dupeParam]`, so a closure
  // capture keeps that undefined forever and the conversion silently falls
  // back to the bare `trackedHash` (see the fire site below for why that
  // collapses every visitor of a product onto one conversion).
  const tempUserIdRef = useRef<string | undefined>(tempUserId)
  tempUserIdRef.current = tempUserId

  // Set by the tracking effect so the page-lifetime effect below can flush it.
  //
  // Both the `pagehide` listener and the unmount flush live here, on EMPTY
  // deps, rather than inside the tracking effect. Two reasons, and both bite:
  // the tracking effect re-runs the moment `router.replace` strips
  // `?dupe=true`, which would tear down a listener that is supposed to guard
  // the whole 3s window; and an effect cleanup cannot tell a dependency change
  // from a real unmount, so flushing there would fire the event immediately on
  // every search and defeat the settle-gate entirely.
  const flushRef = useRef<(() => void) | null>(null)
  useEffect(() => {
    const flush = () => flushRef.current?.()
    window.addEventListener('pagehide', flush)
    return () => {
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [])

  useEffect(() => {
    if (!hash) return
    if (dupeParam !== 'true') return

    const urlParamShopKey = urlParam ? getShopKey(urlParam) : undefined
    const trackedHash = hash

    // Whether THIS surface measures search outcome at all, decided once at
    // effect time. Distinguishes "not measured here" (omit the keys) from
    // "measured, but unknown by the time we fired" (null keys).
    const measuresOutcome = outcomeRef.current.outcome !== undefined

    /** Outcome for the search this effect is tracking, or undefined. */
    const scopedOutcome = (): PerformedDupeOutcome | undefined => {
      const current = outcomeRef.current
      if (current.hash === trackedHash) return current.outcome
      // The page moved on to another search. Report unknown rather than
      // borrowing the new search's numbers.
      return measuresOutcome ? UNSETTLED_OUTCOME : undefined
    }

    // Exactly one event per effect run, whichever path gets there first.
    let fired = false
    const armedAt = Date.now()
    const fire = () => {
      if (fired) return
      fired = true
      const gatesResolved = Date.now() - armedAt >= GATE_GRACE_MS

      if (trackAdsConversions) {
        // ⚠️ `trackedHash` alone is NOT unique per search: it is the md5 of the
        // source product URL, so every visitor of the same product shares it.
        // Google Ads dedupes a conversion action on `transaction_id`, so a
        // product searched by 500 people would report roughly one conversion.
        // The temp user id separates them; read through a ref because it
        // usually arrives a render AFTER this effect ran.
        //
        // ⚠️ No fallback. Sending the bare hash when the id has not settled is
        // the collapse above, and letting the key change between a first fire
        // and a remount would double-count the same search instead. Skipping is
        // the only option that is wrong in neither direction.
        //
        // Per user+product, deliberately, NOT per arrival — two reasons that
        // point the same way.
        //
        // It is not a second search. That product's dupes are cached for ~24h,
        // so re-running it inside the session returns the same set with no new
        // retrieval. Collapsing the repeat is what the conversion MEANS here.
        //
        // And the alternative is worse. Minting a fresh id per arrival would
        // also count a refresh or a client-side re-mount as a new conversion,
        // which no Ads counting setting can undo — an inflated count trains
        // bidding on searches that never happened, where an undercount is only
        // conservative.
        //
        // Chat differs and correctly counts every turn: each turn is a real
        // retrieval, not a cache replay.
        const scopedTempUserId = tempUserIdRef.current
        const currentSearchType =
          searchTypeRef.current.hash === trackedHash
            ? searchTypeRef.current.searchType
            : undefined

        // Only a search that produced something is a conversion, matching the
        // chat surface's `products.length > 0`. `poll` fires this callback on
        // its 3s timeout regardless, and `outcome.ready` counts a FAILED dupes
        // request as settled — so without this a 500 or a zero-result search
        // would report a paid conversion. The `Performed Dupe` event below is
        // deliberately unaffected: it must still record those.
        // Organic rows PLUS curated picks, matching `buildOutcomeProps` above —
        // a search whose title filter drops every organic row but still shows
        // Our Picks did produce results, and reports `zero_results: false`.
        // Counting only the organic rows here would deny it a conversion while
        // the same search calls itself non-empty two lines down.
        const outcomeForAds = scopedOutcome()
        const producedResults =
          (outcomeForAds?.resultsCount ?? 0) +
            (outcomeForAds?.picksCount ?? 0) >
          0

        if (scopedTempUserId && producedResults) {
          const adsSearchId = `${trackedHash}:${scopedTempUserId}`
          const adsSearchType: DupeSearchType = urlParam
            ? 'url_prefix'
            : (currentSearchType ?? 'text')

          trackGoogleAdsDupePerformed({
            searchId: adsSearchId,
            searchType: adsSearchType,
          })
          // Meta and TikTok are capped at ONE PER USER PER DAY, so they do
          // NOT share the Google id above. Google wants per-search (see
          // `googleAdsConversions.ts`); marketing explicitly asked the other
          // two for the first conversion of the day only, because these are
          // ad clicks and a repeat search says nothing new (DUP-2188 for Meta,
          // DUP-2261 for TikTok). `scopedTempUserId` is guaranteed here — the
          // enclosing branch gates on it.
          const cappedConversionId = dailyConversionId(scopedTempUserId)
          // Still the DUP-2091 fix: the pixel copy used to ride the
          // once-per-THREAD `PerformedDupe` emitter below, which under-reported
          // every second-plus search. It fires per search, then caps per day.
          trackMetaDupePerformed({
            conversionId: cappedConversionId,
            searchType: adsSearchType,
          })
          // Meta's SERVER-side twin of the same search, feeding the PostHog
          // "Meta Ads Conversions" CAPI destination. Runs ALONGSIDE the pixel
          // on purpose — redundant transports are Meta's own recommendation,
          // and both stamp `cappedConversionId` so Meta collapses the pair on
          // `event_id` rather than double-counting. Capping only one of them
          // would break that match and let the uncapped one count separately.
          // Deliberately a DIFFERENT
          // PostHog event than the `PerformedDupe` call below, whose chat
          // gate is once-per-THREAD (see `BrainChat.tsx`); a single shared
          // destination-trigger event is simpler to reason about across both
          // surfaces. See `metaAdsConversion.ts` for the dedup.
          trackMetaAdsConversion(analytics.track, {
            transactionId: cappedConversionId,
            hash: trackedHash,
            shopKey: urlParamShopKey,
            threadId,
            search_type: adsSearchType,
          })
          // TikTok's copy of the same search, as its own Zaraz event
          // (`dupe_performed_daily`) because TikTok's cadence is now Meta's,
          // not Google's — two cadences cannot ride one event. Same daily id,
          // so TikTok's `event_id` dedup lines up with our own cap. See
          // `tiktokConversions.ts`.
          trackTikTokDupePerformed({
            conversionId: cappedConversionId,
            searchType: adsSearchType,
          })
          // ChatGPT Ads, on the same daily id as Meta and TikTok. This surface
          // is a sliver of chat's volume and shares the daily cap with it, so
          // it will rarely be the call that produces the day's conversion —
          // it is here so that all four platforms are enrolled at both
          // dispatch points and none of them counts a dupe the other three
          // did not. See `openAiConversions.ts`.
          trackOpenAiDupePerformed({
            conversionId: cappedConversionId,
            searchType: adsSearchType,
          })
        }
      }

      analytics.track(
        AnalyticsEventName.PerformedDupe,
        {
          hash: trackedHash,
          shopKey: urlParamShopKey,
          method: 'dupe.com',
          chat,
          threadId,
          categoryGroup,
          // Where this was rendering, read from `window.location` at FIRE
          // time — not captured on mount, because this fires from a poll
          // callback up to 3s later and a client-side navigation re-renders
          // this component rather than remounting it. `surface` is what
          // separates the extension side panel from any other embed;
          // `in_iframe` on its own cannot, since any site can frame us.
          ...readSearchContextProps(),
          ...buildOutcomeProps(scopedOutcome(), gatesResolved),
          ...(searchTypeRef.current.hash === trackedHash &&
          searchTypeRef.current.searchType
            ? { query_type: QUERY_TYPE[searchTypeRef.current.searchType] }
            : {}),
        },
        {
          sendToPlausible: true,
          sendToTinybird: true,
          // Surfaces enrolled in the ads conversions get their Meta pixel
          // `Performed Dupe` from `trackMetaDupePerformed` above (DUP-2091,
          // per-search + settled-and-produced-results gates) — firing it from
          // here too would double-count them in Meta. The extension sidepanel
          // surfaces don't opt in, so their legacy Meta copy is unchanged.
          sendToFb: !trackAdsConversions,
          sendToPodscribe: true,
        }
      )
    }

    // Wait for Podscribe readiness AND for the dupes to settle before firing,
    // so `results_count` describes a finished search. `poll` is capped at 3s
    // and fires the callback anyway on timeout, so a slow (or failed) dupes
    // request delays this event but never drops it — the count is reported as
    // null in that case, which reads as "unknown", never as a zero-result
    // search. Do not "simplify" that null to 0: the zero-result recovery
    // journey keys on it, and a fabricated zero would email people whose
    // search actually worked.
    // ⚠️ `enabled: false` deliberately does NOT short-circuit this gate, even
    // though a query that never ran has nothing to wait for. `poll` runs its
    // first check SYNCHRONOUSLY, and the gates `enabled` is built from resolve
    // asynchronously — a fresh visitor has no country cookie for the first few
    // hundred ms. Firing on that first check would stamp `never_ran` on every
    // cold load, which is the same false certainty this whole change exists to
    // remove. So a never-enabled search rides the 3s cap like an unsettled
    // one, and `enabled` is re-read at fire time, by which point it is true.
    poll(
      () => !!(window as any).podscribe && (scopedOutcome()?.ready ?? true),
      fire
    )

    // The settle-gate opened a window (up to 3s) in which the page can be
    // destroyed before the event has fired — a click-out on a broadcast
    // surface does exactly that. Hand the flush to the page-lifetime effect
    // above so it survives this effect's own re-runs; `fired` keeps it to one
    // event either way.
    flushRef.current = fire

    // Remove the dupe=true query param using Next.js router
    if (router.query.dupe) {
      const newQuery = { ...router.query }
      delete newQuery.dupe

      router.replace(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      )
    }

    // The `analytics` object causes this useEffect to
    // run multiple times, so we exclude it from the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParam, hash, dupeParam])
}

export default useTrackPerformedDupe
